import Quill from 'quill'

// Get the Embed class from Quill (make sure to cast as any for TS compatibility)
const Embed = Quill.import('blots/embed')

/**
 * Custom MoreButton Blot Implementation
 * Based on the working pattern of CustomMentionBlot
 */
class MoreButtonBlot extends Embed {
    // Define the static properties exactly like CustomMentionBlot
    static blotName = 'more-button'
    static tagName = 'span'
    static className = 'more-button'

    /**
     * Create method is called when Quill needs to create a new instance of this blot
     */
    static create(value) {
        // Always start with super.create for proper inheritance
        const node = super.create()

        // Important: Set contentEditable to false to prevent editing
        node.contentEditable = 'false'

        // Make the node non-focusable
        node.setAttribute('tabindex', '-1')

        // Add data attributes for identification and styling
        node.setAttribute('data-more-button', 'true')
        node.setAttribute('data-button-id', value?.id || 'expand-button')

        // Store the instance ID for scoping
        if (value?.instanceId) {
            node.setAttribute('data-instance-id', value.instanceId)
        }

        // Set inline styles - improved for mobile
        node.style.color = 'rgb(156 147 255)'
        node.style.fontWeight = '600'
        node.style.cursor = 'pointer'
        node.style.display = 'inline-block'
        node.style.padding = '0px 6px' // Slightly increased touch target
        node.style.marginLeft = '4px' // Added space from ellipsis
        node.style.userSelect = 'none' // Prevent text selection
        node.style.outline = 'none' // Remove focus outline
        node.style.WebkitTapHighlightColor = 'transparent' // Remove tap highlight on mobile

        // Create text content
        node.textContent = 'more'

        // Handle click event for desktop and mobile - use one consistent approach
        const handleAction = e => {
            e.preventDefault()
            e.stopPropagation()

            const eventDetail = {
                buttonId: value?.id || 'expand-button',
                instanceId: value?.instanceId || 'unknown',
                target: node, // Include the original node
            }

            // console.log('[MoreButtonBlot] Dispatching event:', eventDetail);

            // Always dispatch to document to ensure it works across different views
            document.dispatchEvent(
                new CustomEvent('more-button-clicked', {
                    bubbles: true,
                    detail: eventDetail,
                }),
            )
        }

        // Add both click and touchstart listeners
        node.addEventListener('click', handleAction)
        node.addEventListener('touchstart', handleAction)

        return node
    }

    /**
     * Value method extracts the data from a DOM node
     */
    static value(node) {
        return {
            id: node.getAttribute('data-button-id') || 'expand-button',
            instanceId: node.getAttribute('data-instance-id') || 'unknown',
        }
    }
}

export default MoreButtonBlot
